<template>
  <div class="galerija-album">
    <header class="galerija-album__header">
      <div class="galerija-album__header-top">
        <router-link :to="{ name: 'Galerija' }" class="galerija-album__back">
          <span class="material-symbols-outlined">arrow_back</span>
          Back to Galerija
        </router-link>
        <button
          v-if="album || isNew"
          type="button"
          class="galerija-album__btn-save"
          @click="saveAll"
        >
          Save
        </button>
      </div>
    </header>

    <div class="galerija-album__body">
      <template v-if="!album && !isNew">
        <p class="galerija-album__empty">Album not found.</p>
      </template>
      <template v-else>
        <div class="galerija-album__form-row">
          <div class="galerija-album__form-section">
            <label class="galerija-album__form-label">Title (Serbian)</label>
            <el-input
              v-model="editName"
              class="galerija-album__form-input"
              placeholder="Album name"
            />
          </div>
          <div class="galerija-album__form-section">
            <label class="galerija-album__form-label">Title (English)</label>
            <el-input
              v-model="editNameEn"
              class="galerija-album__form-input"
              placeholder="Album name (English)"
            />
          </div>
        </div>
        <div class="galerija-album__grid">
          <div
            v-for="(img, index) in editImages"
            :key="`img-${index}-${albumIdOrNew}`"
            class="galerija-album__card"
            :class="{ 'galerija-album__card--dragging': imageDraggedIndex === index }"
            @dragover.prevent="onImageDragOver($event, index)"
            @drop="onImageDrop(index)"
          >
            <div class="galerija-album__card-img-wrap">
              <span
                class="galerija-album__drag-handle"
                draggable="true"
                aria-label="Drag to reorder"
                @dragstart="onImageDragStart($event, index, $event.currentTarget.closest('.galerija-album__card'))"
                @dragend="onImageDragEnd"
                @click.stop
              >
                <span class="galerija-album__drag-lines">
                  <span class="galerija-album__drag-line"></span>
                  <span class="galerija-album__drag-line"></span>
                </span>
              </span>
              <div class="galerija-album__thumb-click" @click="openLightbox(index)">
                <img :src="imageDisplayUrl(img.url)" class="galerija-album__img" alt="">
              </div>
              <button
                type="button"
                class="galerija-album__thumb-remove"
                aria-label="Remove image"
                @click.stop="removeImage(index)"
              >
                <span class="material-symbols-outlined galerija-album__thumb-remove-icon">delete</span>
              </button>
            </div>
            <div class="galerija-album__card-fields">
              <el-input
                :value="img.description || ''"
                placeholder="Description"
                class="galerija-album__card-input"
                @input="(val) => updateImageField(index, 'description', val)"
              />
              <el-input
                :value="img.link || ''"
                placeholder="Link (URL)"
                class="galerija-album__card-input"
                @input="(val) => updateImageField(index, 'link', val)"
              />
            </div>
          </div>
          <button
            type="button"
            class="galerija-album__add-slot"
            aria-label="Add images"
            @click="$refs.uploadFileInput && $refs.uploadFileInput.click()"
          >
            <span class="material-symbols-outlined galerija-album__add-slot-icon">add</span>
            <span class="galerija-album__add-slot-text">+ Add photo</span>
          </button>
        </div>
        <input
          ref="uploadFileInput"
          type="file"
          accept="image/*"
          multiple
          class="galerija-album__file-input"
          @change="onUploadFilesChange"
        >
        <p v-if="!editImages.length" class="galerija-album__empty galerija-album__empty--mt">
          No images in this album. Click + to add images.
        </p>
      </template>
    </div>

    <div
      v-if="lightboxVisible && editImages.length && editImages[lightboxIndex]"
      ref="lightbox"
      tabindex="-1"
      class="galerija-album__lightbox"
      @keydown.esc="closeLightbox"
      @keydown.left.prevent="goToPrevImage"
      @keydown.right.prevent="goToNextImage"
    >
      <button
        type="button"
        class="galerija-album__lightbox-close"
        aria-label="Close"
        @click="closeLightbox"
      >
        <span class="material-symbols-outlined">close</span>
      </button>
      <button
        v-if="editImages.length > 1"
        type="button"
        class="galerija-album__lightbox-prev"
        aria-label="Previous image"
        @click="goToPrevImage"
      >
        <span class="material-symbols-outlined">chevron_left</span>
      </button>
      <img
        :src="imageDisplayUrl(editImages[lightboxIndex].url)"
        class="galerija-album__lightbox-img"
        alt=""
        @click.stop
      >
      <button
        v-if="editImages.length > 1"
        type="button"
        class="galerija-album__lightbox-next"
        aria-label="Next image"
        @click="goToNextImage"
      >
        <span class="material-symbols-outlined">chevron_right</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Message, Input } from 'element-ui'

export default {
  name: 'GalerijaAlbum',

  components: {
    ElInput: Input
  },

  data () {
    return {
      editName: '',
      editNameEn: '',
      editImages: [],
      imageDraggedIndex: null,
      lightboxVisible: false,
      lightboxIndex: 0,
      dragPreviewEl: null
    }
  },

  watch: {
    album: {
      handler (val) {
        this.editName = (val && typeof val.name === 'string') ? val.name : ''
        this.editNameEn = (val && typeof val.nameEn === 'string') ? val.nameEn : ''
        this.editImages = (val && Array.isArray(val.images))
          ? val.images.map((img) => ({ url: img.url, description: img.description || '', link: img.link || '' }))
          : []
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters('galerija', ['albumById']),
    isNew () {
      return this.$route.params.id === 'new'
    },
    album () {
      return this.isNew ? null : (this.albumById(this.$route.params.id) || null)
    },
    albumIdOrNew () {
      return this.album ? this.album.id : 'new'
    }
  },

  mounted () {
    if (!this.album && !this.isNew) this.load()
  },

  methods: {
    ...mapActions('galerija', ['load', 'updateAlbum', 'createAlbumFromEdit', 'uploadGalleryImage']),

    imageDisplayUrl (url) {
      if (!url) return ''
      return url.startsWith('http') || url.startsWith('data:') ? url : (this.$path + (url.startsWith('/') ? url : '/' + url))
    },

    async onUploadFilesChange (e) {
      const files = Array.from(e.target.files || [])
      if (!files.length) return
      if (this.$refs.uploadFileInput) this.$refs.uploadFileInput.value = ''
      try {
        const paths = await Promise.all(
          files.map((file) => this.uploadGalleryImage(file))
        )
        paths.forEach((path) => {
          this.editImages.push({ url: path, description: '', link: '' })
        })
        Message.success('Images added. Click Save to persist.')
      } catch {
        Message.error('Upload failed.')
      }
    },

    onImageDragStart (e, index, thumbElement) {
      this.imageDraggedIndex = index
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
      if (thumbElement) {
        const imgWrap = thumbElement.querySelector('.galerija-album__card-img-wrap')
        const img = imgWrap && imgWrap.querySelector('.galerija-album__img')
        if (imgWrap && img) {
          const rect = imgWrap.getBoundingClientRect()
          const ghost = document.createElement('div')
          ghost.setAttribute('class', 'galerija-album__drag-ghost')
          ghost.style.cssText = `position:fixed;left:${e.clientX}px;top:${e.clientY}px;width:${rect.width}px;height:${rect.height}px;transform:translate(-50%,-50%);overflow:hidden;background:#f1f5f9;pointer-events:none;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,0.15);border-radius:0.5rem;`
          const imgClone = img.cloneNode(true)
          imgClone.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;'
          ghost.appendChild(imgClone)
          document.body.appendChild(ghost)
          this.dragPreviewEl = ghost
          this._boundUpdateDragGhost = (ev) => this.updateDragGhostPosition(ev)
          document.addEventListener('dragover', this._boundUpdateDragGhost)
          const transparent = document.createElement('div')
          transparent.setAttribute('aria-hidden', 'true')
          transparent.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;'
          document.body.appendChild(transparent)
          this._transparentDragImage = transparent
          e.dataTransfer.setDragImage(transparent, 0, 0)
        } else {
          const rect = thumbElement.getBoundingClientRect()
          e.dataTransfer.setDragImage(thumbElement, rect.width / 2, rect.height / 2)
        }
      }
    },

    updateDragGhostPosition (e) {
      if (this.dragPreviewEl) {
        this.dragPreviewEl.style.left = `${e.clientX}px`
        this.dragPreviewEl.style.top = `${e.clientY}px`
      }
    },

    onImageDragEnd () {
      if (this._boundUpdateDragGhost) {
        document.removeEventListener('dragover', this._boundUpdateDragGhost)
        this._boundUpdateDragGhost = null
      }
      if (this._transparentDragImage && this._transparentDragImage.parentNode) {
        this._transparentDragImage.parentNode.removeChild(this._transparentDragImage)
        this._transparentDragImage = null
      }
      if (this.dragPreviewEl && this.dragPreviewEl.parentNode) {
        this.dragPreviewEl.parentNode.removeChild(this.dragPreviewEl)
        this.dragPreviewEl = null
      }
      this.imageDraggedIndex = null
    },

    onImageDragOver (e, index) {
      if (this.imageDraggedIndex === null) return
      e.dataTransfer.dropEffect = 'move'
    },

    onImageDrop (dropIndex) {
      if (this.imageDraggedIndex === null || this.imageDraggedIndex === dropIndex) return
      const item = this.editImages.splice(this.imageDraggedIndex, 1)[0]
      this.editImages.splice(dropIndex, 0, item)
      this.imageDraggedIndex = null
    },

    async saveAll () {
      const name = this.editName && this.editName.trim()
      if (!name) {
        Message.warning('Please enter an album name.')
        return
      }
      const nameEn = (this.editNameEn && this.editNameEn.trim()) ? this.editNameEn.trim() : ''
      if (this.isNew) {
        try {
          const created = await this.createAlbumFromEdit({ name, nameEn })
          const imagesToSave = [...this.editImages]
          if (imagesToSave.length > 0) {
            await this.updateAlbum({ id: created.id, name, nameEn, images: imagesToSave })
          }
          Message.success(imagesToSave.length > 0 ? 'Album created with images.' : 'Album created.')
          this.$router.push({ name: 'Galerija' })
        } catch {
          Message.error('Failed to create album.')
        }
        return
      }
      if (!this.album) return
      try {
        await this.updateAlbum({ id: this.album.id, name, nameEn, images: [...this.editImages] })
        Message.success('Album saved.')
        this.$router.push({ name: 'Galerija' })
      } catch {
        Message.error('Failed to save album.')
      }
    },

    updateImageField (index, field, value) {
      if (index < 0 || index >= this.editImages.length) return
      this.$set(this.editImages, index, { ...this.editImages[index], [field]: value })
    },

    removeImage (index) {
      if (index < 0 || index >= this.editImages.length) return
      this.editImages.splice(index, 1)
      Message.success('Image removed. Click Save to persist.')
    },

    openLightbox (index) {
      this.lightboxIndex = index
      this.lightboxVisible = true
      this.$nextTick(() => {
        if (this.$refs.lightbox) this.$refs.lightbox.focus()
      })
    },

    closeLightbox () {
      this.lightboxVisible = false
    },

    goToPrevImage () {
      if (!this.editImages.length) return
      this.lightboxIndex = this.lightboxIndex <= 0 ? this.editImages.length - 1 : this.lightboxIndex - 1
    },

    goToNextImage () {
      if (!this.editImages.length) return
      this.lightboxIndex = this.lightboxIndex >= this.editImages.length - 1 ? 0 : this.lightboxIndex + 1
    }
  }
}
</script>

<style lang="scss" scoped>
.galerija-album {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  font-family: 'Manrope', sans-serif;
}

.galerija-album__header {
  padding: 2rem 3rem;
  flex-shrink: 0;
  border-bottom: 1px solid #f1f5f9;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(24px);
}

.galerija-album__header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.galerija-album__back {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: color 0.15s;
  .material-symbols-outlined {
    font-size: 1.25rem;
  }
  &:hover {
    color: #0f172a;
  }
}

.galerija-album__btn-save {
  padding: 0.75rem 1.5rem;
  background: #1a1a1a;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover {
    background: #334155;
  }
}

.galerija-album__form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem 2rem;
  margin-bottom: 1.5rem;
}

.galerija-album__form-section {
  flex: 1;
  min-width: 0;
}

.galerija-album__form-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.galerija-album__form-input {
  max-width: 28rem;
  :deep(.el-input__inner) {
    border-radius: 0.625rem;
    border: 1px solid #ced4da;
    background: #fff;
    color: #495057;
    padding: 0.5rem 0.75rem;
  }
}

.galerija-album__file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.galerija-album__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 2rem 3rem;
}

.galerija-album__empty {
  color: #64748b;
  font-size: 0.9375rem;
  margin: 0;
  &--mt {
    margin-top: 1rem;
  }
}

.galerija-album__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.galerija-album__card {
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  overflow: hidden;
  background: #fff;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  &.galerija-album__card--dragging {
    opacity: 0.6;
  }
}

.galerija-album__card-img-wrap {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  background: #f1f5f9;
  &:hover .galerija-album__thumb-remove {
    opacity: 1;
  }
}

.galerija-album__card-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
}

.galerija-album__card-input {
  :deep(.el-input__inner) {
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
  }
}

.galerija-album__add-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 12rem;
  aspect-ratio: 1;
  padding: 0;
  color: #94a3b8;
  background: #fff;
  border: 1px solid #bfdbfe;
  border-radius: 1rem;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  .galerija-album__add-slot-icon {
    font-size: 2.5rem;
  }
  .galerija-album__add-slot-text {
    font-size: 0.875rem;
    font-weight: 500;
  }
  &:hover {
    color: #64748b;
    background: #f8fafc;
    border-color: #93c5fd;
  }
}

.galerija-album__drag-handle {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  background: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  &:active {
    cursor: grabbing;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.85);
  }
}

.galerija-album__drag-lines {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.1875rem;
  width: 100%;
  height: 100%;
}

.galerija-album__drag-line {
  width: 0.375rem;
  height: 0.125rem;
  background: #0f172a;
  border-radius: 0.0625rem;
}

.galerija-album__thumb-click {
  position: absolute;
  inset: 0;
  cursor: pointer;
  display: block;
}

.galerija-album__thumb-remove {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 2;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e05452;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;
  &:hover {
    background: #c94a48;
  }
  .galerija-album__thumb-remove-icon {
    font-size: 1rem;
    color: #fff;
  }
}

.galerija-album__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.galerija-album__lightbox {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.92);
  outline: none;
  &:focus {
    outline: none;
  }
}

.galerija-album__lightbox-close {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0.5rem;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;
  .material-symbols-outlined {
    font-size: 1.5rem;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
}

.galerija-album__lightbox-prev,
.galerija-album__lightbox-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  padding: 0;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;
  .material-symbols-outlined {
    font-size: 2rem;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.galerija-album__lightbox-prev {
  left: 1.5rem;
}

.galerija-album__lightbox-next {
  right: 1.5rem;
}

.galerija-album__lightbox-img {
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
  height: auto;
  object-fit: contain;
}
</style>

<style lang="scss">
/* Drag ghost lives in body; keep image sharp (no browser drag blur) */
.galerija-album__drag-ghost {
  img {
    image-rendering: auto;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }
}
</style>
